// Libs
import classNames from 'classnames/bind';
import { Button, Flex, Heading, IconRefreshLine, Spinner, Text, Tooltip, TruncateText, View } from '@instructure/ui';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
// Components, Layouts, Pages
import ModalUploadAvatar from './component/ModalUploadAvatar';
// Others
import { EMPTY_STRING, GENERATE_AVATAR_BASE_URL, REG_EXP } from '~/utils/constants/common';
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { getProfile } from '~/thunks/user/userThunk';
import { PaymentStatusEnum } from '~/utils/enum/base';
import { studentRoute } from '~/utils/routes/student';
// Styles, images, icons
import styles from './Information.module.scss';
import './Information.scss';
import { icons } from '~/assets';

type Props = {
  hideIcons?: boolean;
};

const cx = classNames.bind(styles);

const Information = (props: Props) => {
  //#region Destructuring Props
  const { hideIcons } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/home', 'common/base', 'admin/tuition', 'admin/administrativeArea']);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  //#endregion Declare Hook

  //#region Selector
  const userProfile = useAppSelector((state) => state.userState.profile);
  const isRefresh = useAppSelector((state) => state.userState.isRefresh);
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetProfile();
  }, []);

  useEffect(() => {
    handleGetProfile();
  }, [isRefresh]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetProfile = async () => {
    setIsLoading(true);
    setErrorMessage(null);

    try {
      await dispatch(getProfile()).unwrap();
    } catch (e) {
      setErrorMessage((e as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  const getAvatarUrl = () => {
    if (!userProfile) return;

    if (userProfile?.avatarUrl) return userProfile?.avatarUrl;

    const encodedName = encodeURIComponent(userProfile?.firstName ?? '').replace(REG_EXP.QUERY, '+');
    return `${GENERATE_AVATAR_BASE_URL}?name=${encodedName}`;
  };

  const renderContent = () => {
    if (isLoading && !userProfile) {
      return (
        <div className={cx('centerContainer')}>
          <Spinner size='small' />
        </div>
      );
    }

    if (errorMessage && !userProfile) {
      return (
        <div className={cx('centerContainer')}>
          <Flex justifyItems='center' margin='medium 0'>
            <Button
              onClick={handleGetProfile}
              size='medium'
              color='primary'
              margin='x-large 0 0 0'
              renderIcon={<IconRefreshLine />}
            >
              {t('retry_button_label', { ns: 'common/base' })}
            </Button>
          </Flex>
        </div>
      );
    }

    if (!userProfile) {
      return <></>;
    }

    return (
      <>
        <div className={cx('mainContainer')}>
          <div className={cx('viewContent')}>
            <Heading level='h3' margin='0 0 small 0'>
              {t('student_info_name_title', {
                studentName: userProfile?.firstName,
                studentSurname: userProfile?.lastName,
              })}
            </Heading>

            <Flex direction='row' gap='small'>
              <Text variant='contentImportant'>{t('student_info_id_label')}</Text>
              <Text variant='content'>{userProfile?.id}</Text>
            </Flex>

            <Flex direction='row' gap='small' margin='xx-small 0 0 0' alignItems='start'>
              <Text variant='contentImportant'>
                <View as='div' className={cx('nowrap')}>
                  {t('student_info_assigned_courses_label')}
                </View>
              </Text>

              {userProfile?.fieldOfStudy ? (
                <TruncateText maxLines={2}>
                  <div className={cx('fieldOfStudy')}>{userProfile?.fieldOfStudy}</div>
                </TruncateText>
              ) : (
                <Text>{EMPTY_STRING}</Text>
              )}
            </Flex>

            {/* <Flex direction='row' gap='small' margin='xx-small 0 0 0'>
                <Text variant='contentImportant'>{t('student_info_year_of_study_label')}</Text>
                <Text variant='content'>{userProfile?.yearOfStudy}</Text>
              </Flex> */}

            <Flex direction='row' alignItems='center' margin='small 0'>
              <img
                src={
                  userProfile?.status === PaymentStatusEnum.PAYMENT_COMPLETED
                    ? icons.commonIconChangeStatusActive
                    : icons.commonIconChangeStatusInactive
                }
                alt={t('common_alt_img', { ns: 'common/base' })}
                className={cx('iconStatus')}
              />
              <Flex direction='row' gap='small'>
                <Text variant='contentImportant'>{t('student_info_status_label')}</Text>
                <Text>
                  {userProfile?.status === PaymentStatusEnum.PAYMENT_COMPLETED
                    ? t('student_info_payment_completed_status_label')
                    : t('student_info_waiting_payment_status_label')}
                </Text>
              </Flex>
            </Flex>
          </div>

          <div className={cx('avatarContainer')}>
            <View className={cx('imageContainer')}>
              <img
                src={getAvatarUrl()}
                alt={t('common_alt_img', { ns: 'common/base' })}
                className={cx('studentAvatar')}
              />

              {!hideIcons && (
                <View className={`${cx('iconUploadContainer')} roundIcon`}>
                  <Tooltip renderTip={t('upload_image_title_label', { ns: 'common/base' })}>
                    <Button
                      onClick={handleOpenUploadImage}
                      renderIcon={
                        <img
                          className={cx('iconUpload')}
                          src={icons.commonIconUploadFile}
                          alt={t('common_alt_img', { ns: 'common/base' })}
                        />
                      }
                    ></Button>
                  </Tooltip>
                </View>
              )}
            </View>

            {!hideIcons && (
              <div className={cx('iconGroup')}>
                <View className={'roundIcon'}>
                  <Tooltip renderTip={t('tuition_area_title', { ns: 'admin/tuition' })}>
                    <Button
                      onClick={handleNavigateTuitionFees}
                      renderIcon={
                        <img
                          src={icons.commonIconTuitionArea}
                          alt={t('common_alt_img', { ns: 'common/base' })}
                          className={cx('iconScholarship')}
                        />
                      }
                    ></Button>
                  </Tooltip>
                </View>

                <View className={'roundIcon'}>
                  <Tooltip renderTip={t('administrative_area_title', { ns: 'admin/administrativeArea' })}>
                    <Button
                      onClick={handleNavigateEducationalProgram}
                      renderIcon={
                        <img
                          src={icons.commonIconExamArea}
                          alt={t('common_alt_img', { ns: 'common/base' })}
                          className={cx('iconStudy')}
                        />
                      }
                    ></Button>
                  </Tooltip>
                </View>
              </div>
            )}
          </div>
        </div>

        <Flex direction='column' gap='xxx-small' width={'100%'}>
          <Text variant='contentImportant'>{t('student_info_study_program')}</Text>

          <View as='div' width={'100%'} className={cx('progressBar')}>
            <div
              style={{ width: `${(userProfile!.totalPassedExam / userProfile!.totalExam) * 100}%` }}
              className={cx('valueProgressBar')}
            ></div>
            <View as='div' className={cx('labelProgressBar')}>
              <Text variant='contentImportant'>
                {t('student_info_value_program', {
                  currentValue: userProfile!.totalPassedExam,
                  maxValue: userProfile!.totalExam,
                })}
              </Text>
            </View>
          </View>
        </Flex>
      </>
    );
  };

  const handleOpenUploadImage = () => {
    setIsModalOpen(true);
  };

  const handleNavigateTuitionFees = () => {
    navigate(`${studentRoute.base}${studentRoute.tuitionArea}`);
  };

  const handleNavigateEducationalProgram = () => {
    navigate(`${studentRoute.base}${studentRoute.examArea}`);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };
  //#endregion Handle Function

  return (
    <div className={cx('container')}>
      {renderContent()}

      <ModalUploadAvatar isModalOpen={isModalOpen} onClose={handleCloseModal} />
    </div>
  );
};

export default Information;
